import React, { useEffect, useRef, useCallback } from 'react';
import { IoVideocamOutline } from "react-icons/io5";
import { AiOutlineDownload } from "react-icons/ai";
import { FaRegCopy } from "react-icons/fa6";
import { PiArrowBendRightDownBold } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import { getDisplayStyle } from './TagConfig';

const VideoBento = ({ videos, currentPage, videosPerPage, totalVideos, handleLoadMore }) => {
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const [copiedId, setCopiedId] = React.useState(null);

  const resizeGridItem = useCallback((item) => {
    try {
      if (!gridRef.current) return;
      
      const rowHeight = parseInt(window.getComputedStyle(gridRef.current).getPropertyValue('grid-auto-rows'));
      const rowGap = parseInt(window.getComputedStyle(gridRef.current).getPropertyValue('gap'));
      const contentEl = item.querySelector('.content');
      
      if (!contentEl) return;
      
      const contentHeight = contentEl.getBoundingClientRect().height;
      const rowSpan = Math.ceil((contentHeight + rowGap) / (rowHeight + rowGap));
      item.style.gridRowEnd = `span ${rowSpan}`;
    } catch (error) {
      console.error('Error resizing grid item:', error);
    }
  }, []);

  const resizeAllGridItems = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    resizeTimeoutRef.current = setTimeout(() => {
      try {
        const allItems = document.getElementsByClassName("bento-grid-item");
        for (let x = 0; x < allItems.length; x++) {
          resizeGridItem(allItems[x]);
        }
      } catch (error) {
        console.error('Error resizing all grid items:', error);
      }
    }, 100);
  }, [resizeGridItem]);

  const handleVideoLoad = useCallback((event) => {
    const gridItem = event.target.closest('.bento-grid-item');
    if (gridItem) {
      resizeGridItem(gridItem);
      resizeAllGridItems();
    }
  }, [resizeGridItem, resizeAllGridItems]);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        resizeAllGridItems();
      }, 100);
    });

    if (gridRef.current) {
      observer.observe(gridRef.current);
      resizeAllGridItems();
    }

    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(resizeAllGridItems, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, [resizeAllGridItems]);

  useEffect(() => {
    resizeAllGridItems();
  }, [videos, resizeAllGridItems]);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(err => {
      console.error('Could not copy text: ', err);
    });
  };

  const truncateText = (text, maxWords) => {
    if (!text) return '';
    const words = text.split(' ');
    if (words.length > maxWords) {
      return words.slice(0, maxWords).join(' ') + '...';
    }
    return text;
  };

  return (
    <div className="w-full max-w-7xl mx-0 px-4 sm:px-6 lg:px-8 mt-6 pb-44">
      <div 
        ref={gridRef} 
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 auto-rows-[10px]"
      >
        {videos.length === 0 ? (
          <div className="col-span-full flex flex-col items-center justify-center min-h-[60vh] text-black">
            <p className="text-3xl font-medium mb-4">Enter your prompt below!</p>
            <PiArrowBendRightDownBold className="w-12 h-12" />
          </div>
        ) : (
          videos.map((video) => (
            <div key={video.jobId} className="bento-grid-item">
              <div className="content">
                {video.status === 'Completed' && video.video_url ? (
                  <div className="relative group rounded-xl overflow-hidden bg-white shadow-sm transition duration-300 ease-in-out hover:shadow-lg">
                    <video
                      src={video.video_url}
                      className="w-full h-auto"
                      controls
                      onLoadedMetadata={handleVideoLoad}
                      playsInline
                    />
                    <div className="absolute top-3 right-3 flex gap-2 z-10">
                      <button 
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = video.video_url;
                          link.download = `${video.inputPrompt ? video.inputPrompt.slice(0, 30) : 'video'}.mp4`;
                          link.target = '_blank';
                          link.rel = 'noopener noreferrer';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        className="bg-white/10 backdrop-blur-md p-1.5 rounded-full cursor-pointer hover:bg-white/20 transition-all duration-300 border border-white/20"
                      >
                        <AiOutlineDownload className="w-3.5 h-3.5 text-white" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="relative rounded-xl overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 aspect-[9/16]">
                    {video.status === 'Processing' ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="relative w-24 h-24">
                          <div className="absolute inset-0 rounded-full border-4 border-gray-200"></div>
                          <div className="absolute inset-0 rounded-full border-4 border-black border-t-transparent animate-spin"></div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <IoVideocamOutline className="w-8 h-8 text-black" />
                          </div>
                        </div>
                        <div className="mt-6 space-y-2 text-center">
                          <p className="text-base font-medium text-gray-900">Creating your video</p>
                          <p className="text-sm text-gray-500">
                            This usually takes {video.model === 'Standard' ? '1-2' : '2-3'} minutes
                          </p>
                        </div>
                      </div>
                    ) : video.status === 'Failed' ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                        <p className="text-sm font-medium text-gray-900 text-center">
                          There was an error generating your video. Please try again.
                        </p>
                      </div>
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                        <div className="w-12 h-12 rounded-full bg-blue-100 mb-4 flex items-center justify-center">
                          <IoVideocamOutline className="w-6 h-6 text-blue-600" />
                        </div>
                        <p className="text-sm font-medium text-gray-900">Processing video...</p>
                      </div>
                    )}
                  </div>
                )}
                <div className="p-4 relative bg-white rounded-b-xl border-t border-gray-100">
                  <p className="text-sm text-gray-800 font-medium pr-8 mb-2">
                    {video.inputPrompt ? truncateText(video.inputPrompt, 20) : 'No prompt available'}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {video.imageUploaded && (
                      <span className="px-2 py-0.5 text-[11px] font-medium bg-green-50 text-green-600 rounded-full">
                        Image to Video
                      </span>
                    )}
                    {!video.imageUploaded && video.style && getDisplayStyle(video.style) && (
                      <span className="px-2 py-0.5 text-[11px] font-medium bg-blue-50 text-blue-600 rounded-full">
                        Style: {getDisplayStyle(video.style)}
                      </span>
                    )}
                    {video.model && (
                      <span className="px-2 py-0.5 text-[11px] font-medium bg-purple-50 text-purple-600 rounded-full">
                        Model: {video.model}
                      </span>
                    )}
                  </div>
                  {video.inputPrompt && (
                    <button 
                      onClick={() => copyToClipboard(video.inputPrompt, video.jobId)} 
                      className={`absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-400 ${copiedId === video.jobId ? 'text-green-500' : ''}`}
                    >
                      <FaRegCopy size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {totalVideos > currentPage * videosPerPage && (
        <div className="flex justify-center mt-8 mb-4">
          <button
            onClick={handleLoadMore}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 rounded-lg border border-gray-200 shadow-sm transition-colors"
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );
};

export default VideoBento; 